/**
 * The page a patient lands on from their share link.
 *
 * Standalone by design: no app chrome, no auth, no react-query cache. It is
 * registered directly in App.tsx rather than via the publicPages array, since
 * every entry there is wrapped in PublicRoutes (which redirects logged-in users
 * to /home, stopping a clinician from opening a link they just created) and in
 * DefaultLayout (app chrome the recipient has no use for).
 *
 * What the patient sees is the clinic's own summary report, the same document
 * "Summary report (with images)" produces from the export menu, rendered from
 * the same template through the share's redaction profile. Restating the
 * findings in bespoke markup would mean a second thing to keep correct, and the
 * patient would be reading something no clinician has ever looked at.
 *
 * It arrives as HTML, not as an embedded PDF. These links are opened on phones,
 * where an inline PDF frequently renders as nothing at all. The PDF is still
 * one tap away for anyone who wants a copy to keep.
 *
 * The link is single use, which makes holding on to the session the difference
 * between a refresh and a lockout. It lives in sessionStorage, so it survives a
 * reload or a rotated phone and dies when the tab closes - the borrowed-phone
 * case - without ever touching localStorage. Nothing here calls the close
 * endpoint on unload, because a reload fires that too and would spend the link
 * for nothing; the 30 minute server-side expiry is the backstop, and the Close
 * button is how she ends it deliberately.
 *
 * Mobile-first: these arrive over a messaging app and are read on a phone.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import JSZip from 'jszip';

import logo from '../../../small_deepecho_icon.png';
import type {
  ShareChallengeField,
  ShareContentScope,
} from '../../../helpers/api/reports/exam-share';
import {
  closeShareSession,
  fetchSharedReport,
  fetchSharedReportHtml,
  fetchSharedReportPdf,
  probeShare,
  unlockShare,
} from '../../../helpers/api/reports/exam-share';

type Phase =
  | 'checking'
  | 'locked'
  | 'unavailable'
  | 'loading'
  | 'ready'
  // Her session is gone and the link will not open again. Distinct from
  // `unavailable`, which is a link that never opened for her in the first place:
  // she watched this one work, so "no longer available" would read as a fault.
  | 'ended'
  | 'failed'
  | 'closed';

/** Why a content load ended the way it did.
 *
 * `session-rejected` is the server refusing this session; `unreachable` says
 * nothing about it and must not cost her one that still works. */
type LoadOutcome = 'ok' | 'session-rejected' | 'unreachable';

/** Per-tab, so a reload keeps the session but closing the tab drops it.
 *  Keyed by token: two links open in two tabs must not overwrite each other. */
const storageKey = (token: string) => `deepecho.share.${token}`;

function readStoredSession(
  token: string
): { session: string; scope: ShareContentScope } | null {
  try {
    const raw = window.sessionStorage.getItem(storageKey(token));
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    return parsed?.session ? parsed : null;
  } catch {
    // Private browsing modes throw on sessionStorage. Losing the resume is
    // survivable; crashing the page is not.
    return null;
  }
}

function writeStoredSession(
  token: string,
  session: string,
  scope: ShareContentScope
): void {
  try {
    window.sessionStorage.setItem(
      storageKey(token),
      JSON.stringify({ session, scope })
    );
  } catch {
    /* nothing to do: the page still works, it just will not survive a reload */
  }
}

function clearStoredSession(token: string): void {
  try {
    window.sessionStorage.removeItem(storageKey(token));
  } catch {
    /* see above */
  }
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen w-full bg-slate-50 px-4 py-8 sm:py-14">
      <div className="mx-auto flex w-full max-w-xl flex-col gap-6">
        {children}
      </div>
    </div>
  );
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-7">
      {children}
    </div>
  );
}

/**
 * One card, optically centred, under the clinic's mark.
 *
 * Every state that is not the report itself is a single short message, and a
 * single short message pinned to the top of an empty screen reads like a page
 * that failed to finish loading. Centring it makes it the answer rather than a
 * leftover. Slightly above true centre (`pb` heavier than `pt`) because a block
 * placed at the exact middle looks low.
 */
function StatusShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen w-full flex-col items-center justify-center bg-slate-50 px-4 pb-14 pt-10">
      <div className="w-full max-w-md">
        <img
          src={logo}
          alt="Deep Echo"
          className="mx-auto mb-7 h-10 w-auto"
        />
        {children}
      </div>
    </div>
  );
}

/** Neutral is a wait, notice is a link that is over, done is her own doing, and
 *  problem is ours. The patient should read the colour before the sentence. */
type StatusTone = 'neutral' | 'notice' | 'done' | 'problem';

const TONE_BADGE: Record<StatusTone, string> = {
  neutral: 'bg-slate-100 text-slate-500 ring-slate-50',
  notice: 'bg-amber-50 text-amber-600 ring-amber-50/60',
  done: 'bg-emerald-50 text-emerald-600 ring-emerald-50/60',
  problem: 'bg-rose-50 text-rose-600 ring-rose-50/60',
};

/* Inline paths rather than an icon font: this page is the one thing in the app
   a stranger loads on a phone, and it should not wait on a webfont to say what
   happened. */
const ICON_LINK_OFF = (
  <>
    <path d="M9 17H7.5a5 5 0 0 1-1.1-9.9" />
    <path d="M14.6 7H16.5a5 5 0 0 1 3.6 8.5" />
    <path d="M9.5 12H12" />
    <path d="M3 3l18 18" />
  </>
);

const ICON_CLOCK = (
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7.5V12l2.8 1.8" />
  </>
);

const ICON_SHIELD_CHECK = (
  <>
    <path d="M12 3.2l6.8 2.6v5.4c0 4.1-2.8 7.5-6.8 8.4-4-.9-6.8-4.3-6.8-8.4V5.8z" />
    <path d="M9.3 12.1l1.9 1.9 3.6-3.7" />
  </>
);

const ICON_CLOUD_OFF = (
  <>
    <path d="M3 3l18 18" />
    <path d="M8.7 8.1A4.5 4.5 0 0 1 17 9.8a3.6 3.6 0 0 1 2.7 5.9" />
    <path d="M16.2 18H7.4a4.1 4.1 0 0 1-1.3-8" />
  </>
);

function StatusIcon({
  tone,
  children,
}: {
  tone: StatusTone;
  children: React.ReactNode;
}) {
  return (
    <span
      aria-hidden="true"
      className={`mx-auto flex h-14 w-14 items-center justify-center rounded-full ring-8 ${TONE_BADGE[tone]}`}
    >
      <svg
        viewBox="0 0 24 24"
        className="h-6 w-6"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {children}
      </svg>
    </span>
  );
}

function StatusScreen({
  tone,
  icon,
  title,
  body,
  action,
}: {
  tone: StatusTone;
  icon: React.ReactNode;
  title: string;
  body: string;
  action?: React.ReactNode;
}) {
  return (
    <StatusShell>
      <div className="rounded-2xl border border-slate-200 bg-white px-6 py-9 text-center shadow-sm shadow-slate-200/70 sm:px-9 sm:py-11">
        <StatusIcon tone={tone}>{icon}</StatusIcon>
        <h1 className="mt-5 text-xl font-semibold tracking-tight text-slate-900 [text-wrap:balance]">
          {title}
        </h1>
        <p className="mx-auto mt-2.5 max-w-xs text-sm leading-relaxed text-slate-600">
          {body}
        </p>
        {action && <div className="mt-7">{action}</div>}
      </div>
    </StatusShell>
  );
}

/** Waiting states. Same frame as the rest, so nothing jumps when the answer
 *  arrives and replaces it. */
function PendingScreen({ label }: { label: string }) {
  return (
    <StatusShell>
      <div className="rounded-2xl border border-slate-200 bg-white px-6 py-12 text-center shadow-sm shadow-slate-200/70">
        <span
          role="status"
          aria-label={label}
          className="mx-auto block h-7 w-7 animate-spin rounded-full border-2 border-slate-200 border-t-blue-600"
        />
        <p className="mt-5 text-sm text-slate-500">{label}</p>
      </div>
    </StatusShell>
  );
}

export function SharedReportPage() {
  const { token = '' } = useParams<{ token: string }>();
  const { t, i18n } = useTranslation('Report');
  const locale = i18n.resolvedLanguage || i18n.language || 'en-US';

  const [phase, setPhase] = useState<Phase>('checking');
  const [fields, setFields] = useState<ShareChallengeField[]>(['date_of_birth']);
  const [dob, setDob] = useState('');
  const [nationalId, setNationalId] = useState('');
  const [session, setSession] = useState<string | null>(null);
  const [scope, setScope] = useState<ShareContentScope | null>(null);
  const [html, setHtml] = useState<string | null>(null);
  const [previews, setPreviews] = useState<any[]>([]);
  const [attemptFailed, setAttemptFailed] = useState(false);
  // Set when a live link outlived this browser's session, so the unlock form can
  // say why she is being asked again rather than looking like it forgot her.
  const [sessionEnded, setSessionEnded] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadFailed, setDownloadFailed] = useState(false);
  const frameRef = React.useRef<HTMLIFrameElement | null>(null);

  const needsNationalId = fields.includes('national_id');

  /** Pull whatever this scope is allowed to show and move to `ready`.
   *
   * Reports why it failed rather than picking a phase itself: what a failure
   * means depends on whether the server refused the session or was simply not
   * reachable, and only `explainContentFailure` decides what to say. */
  const loadContent = useCallback(
    async (
      sessionToken: string,
      contentScope: ShareContentScope
    ): Promise<LoadOutcome> => {
      setPhase('loading');
      try {
        if (contentScope === 'images_only') {
          // No document for this scope: the summary template renders its
          // clinical sections unconditionally, so the only thing it can produce
          // is the one this scope withholds.
          const data = await fetchSharedReport(sessionToken);
          setPreviews(Array.isArray(data?.previews) ? data.previews : []);
        } else {
          setHtml(await fetchSharedReportHtml(sessionToken, locale));
        }
        setPhase('ready');
        return 'ok';
      } catch (error: any) {
        const status = error?.response?.status;
        return typeof status === 'number' && status >= 400 && status < 500
          ? 'session-rejected'
          : 'unreachable';
      }
    },
    [locale]
  );

  /** Ask the server what this link is and land on the phase that says so. */
  const checkLink = useCallback(
    async (isStale: () => boolean = () => false) => {
      setPhase('checking');
      const probe = await probeShare(token);
      if (isStale()) {
        return;
      }
      if (probe.state === 'active') {
        setFields(probe.fields);
        setPhase('locked');
        return;
      }
      setPhase(probe.state === 'unusable' ? 'unavailable' : 'failed');
    },
    [token]
  );

  /** Work out why the report would not load, and tell her that instead of guessing.
   *
   * Three different things land here and they must not share one message: a link
   * that is finished, a session that expired under a link still willing to open,
   * and a network that dropped.
   *
   * A rejected session is the only case that justifies discarding hers. Note that
   * the link being spent is *not* evidence her session is dead: her own unlock is
   * what spent it, so probing the link is only meaningful once the server has
   * already refused the session. */
  const explainContentFailure = useCallback(
    async (outcome: LoadOutcome) => {
      if (outcome !== 'session-rejected') {
        setPhase('failed');
        return;
      }

      const probe = await probeShare(token);
      if (probe.state === 'unreachable') {
        // No verdict on the link, so do not claim one.
        setPhase('failed');
        return;
      }

      clearStoredSession(token);
      setSession(null);

      if (probe.state === 'active') {
        // The link still opens; only this browser's session is gone. Rare on a
        // single-use link, but a deployment that raised max_unlocks gets here.
        setFields(probe.fields);
        setSessionEnded(true);
        setPhase('locked');
        return;
      }

      setPhase('ended');
    },
    [token]
  );

  // Resume before asking. A single-use link has nothing left to unlock, so on a
  // refresh the stored session is the only way back to the report.
  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setPhase('unavailable');
      return;
    }

    const stored = readStoredSession(token);
    if (stored) {
      setSession(stored.session);
      setScope(stored.scope);
      void loadContent(stored.session, stored.scope).then(outcome => {
        if (!cancelled && outcome !== 'ok') {
          void explainContentFailure(outcome);
        }
      });
      return;
    }

    void checkLink(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [token, loadContent, checkLink, explainContentFailure]);

  const handleUnlock = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!dob || submitting || (needsNationalId && !nationalId.trim())) {
        return;
      }
      setSubmitting(true);
      setAttemptFailed(false);
      setSessionEnded(false);
      try {
        const result = await unlockShare(token, {
          date_of_birth: dob,
          ...(needsNationalId ? { national_id: nationalId.trim() } : {}),
        });
        setSession(result.session_token);
        setScope(result.content_scope);
        // Stored before the content loads: the link is spent either way, and a
        // render failure must not cost her the session as well.
        writeStoredSession(token, result.session_token, result.content_scope);
        setSubmitting(false);
        const outcome = await loadContent(
          result.session_token,
          result.content_scope
        );
        if (outcome !== 'ok') {
          void explainContentFailure(outcome);
        }
      } catch {
        // The API returns one shape for every failure, so the copy here stays
        // deliberately non-committal about which one it was.
        setAttemptFailed(true);
        setPhase('locked');
        setSubmitting(false);
      }
    },
    [
      dob,
      nationalId,
      needsNationalId,
      token,
      submitting,
      loadContent,
      explainContentFailure,
    ]
  );

  const handleCloseSession = useCallback(() => {
    if (session) {
      void closeShareSession(session);
    }
    clearStoredSession(token);
    setSession(null);
    setHtml(null);
    setPreviews([]);
    setPhase('closed');
  }, [session, token]);

  const handleRetry = useCallback(() => {
    if (session && scope) {
      void loadContent(session, scope).then(outcome => {
        if (outcome !== 'ok') {
          void explainContentFailure(outcome);
        }
      });
      return;
    }
    // No session, so the failure was the opening probe. Ask the server again.
    void checkLink();
  }, [session, scope, loadContent, checkLink, explainContentFailure]);

  const handleDownload = useCallback(async () => {
    if (!session || downloading) {
      return;
    }
    setDownloading(true);
    setDownloadFailed(false);
    try {
      const blob = await fetchSharedReportPdf(session, locale);
      const href = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href;
      a.download = 'ultrasound-report.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(href);
    } catch {
      setDownloadFailed(true);
    } finally {
      setDownloading(false);
    }
  }, [session, locale, downloading]);

  /** Hand a blob to the browser as a file.
   *
   *  Always via an object URL, never the data URI directly: `download` is
   *  honoured far more consistently for blob: than for data:, and a long data
   *  URI is what some browsers refuse outright. */
  const saveBlob = useCallback((blob: Blob, filename: string) => {
    const href = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = href;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(href);
  }, []);

  /** Save one image. There is no PDF for an images-only share, so this and the
   *  zip below are the only ways the patient gets to keep anything. */
  const downloadImage = useCallback(
    async (url: string, index: number) => {
      try {
        saveBlob(await (await fetch(url)).blob(), `ultrasound-image-${index}.jpg`);
      } catch {
        setDownloadFailed(true);
      }
    },
    [saveBlob]
  );

  /** Save every image as one zip.
   *
   *  A zip rather than a loop over the individual downloads: browsers block
   *  files after the first when several are triggered together, so a loop
   *  silently saves one and looks broken. */
  const downloadAllImages = useCallback(async () => {
    if (downloading) {
      return;
    }
    setDownloading(true);
    setDownloadFailed(false);
    try {
      const zip = new JSZip();
      for (let i = 0; i < previews.length; i++) {
        const blob = await (await fetch(previews[i].url)).blob();
        zip.file(`ultrasound-image-${i + 1}.jpg`, blob);
      }
      saveBlob(await zip.generateAsync({ type: 'blob' }), 'ultrasound-images.zip');
    } catch {
      setDownloadFailed(true);
    } finally {
      setDownloading(false);
    }
  }, [previews, downloading, saveBlob]);

  /** Grow the frame to its content so the page scrolls, not a box inside it.
   *  A nested scroll area is miserable on a phone. */
  const fitFrame = useCallback(() => {
    const doc = frameRef.current?.contentDocument;
    if (doc?.documentElement && frameRef.current) {
      frameRef.current.style.height = `${doc.documentElement.scrollHeight}px`;
    }
  }, []);

  // Rotating a phone reflows the report, so the measured height goes stale.
  useEffect(() => {
    if (!html) {
      return;
    }
    window.addEventListener('resize', fitFrame);
    return () => window.removeEventListener('resize', fitFrame);
  }, [html, fitFrame]);

  if (phase === 'checking') {
    return <PendingScreen label={t('sharedPage.checking')} />;
  }

  if (phase === 'unavailable') {
    return (
      <StatusScreen
        tone="notice"
        icon={ICON_LINK_OFF}
        title={t('sharedPage.unavailableTitle')}
        body={t('sharedPage.unavailableBody')}
      />
    );
  }

  if (phase === 'ended') {
    return (
      <StatusScreen
        tone="notice"
        icon={ICON_CLOCK}
        title={t('sharedPage.endedTitle')}
        body={t('sharedPage.endedBody')}
      />
    );
  }

  if (phase === 'closed') {
    return (
      <StatusScreen
        tone="done"
        icon={ICON_SHIELD_CHECK}
        title={t('sharedPage.closedTitle')}
        body={t('sharedPage.closedBody')}
      />
    );
  }

  if (phase === 'locked') {
    // The input is 16px or larger on purpose: iOS Safari zooms the whole page in
    // on focus for anything smaller, and it never zooms back out.
    const fieldClass =
      'w-full rounded-lg border border-slate-300 px-3 py-2.5 text-base text-slate-800 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200';

    return (
      <StatusShell>
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm shadow-slate-200/70 sm:p-8">
          <h1 className="text-xl font-semibold tracking-tight text-slate-900 [text-wrap:balance]">
            {t('sharedPage.unlockTitle')}
          </h1>
          <p className="mt-2 text-sm leading-relaxed text-slate-600">
            {needsNationalId
              ? t('sharedPage.unlockBodyWithId')
              : t('sharedPage.unlockBody')}
          </p>

          {sessionEnded && (
            <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              {t('sharedPage.sessionEndedNotice')}
            </p>
          )}

          <form onSubmit={handleUnlock} className="mt-5 flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="share-dob"
                className="text-sm font-medium text-slate-700"
              >
                {t('sharedPage.dobLabel')}
              </label>
              <input
                id="share-dob"
                type="date"
                required
                value={dob}
                onChange={e => setDob(e.target.value)}
                aria-invalid={attemptFailed}
                aria-describedby={attemptFailed ? 'share-unlock-error' : undefined}
                className={fieldClass}
              />
            </div>

            {needsNationalId && (
              <div className="flex flex-col gap-1.5">
                <label
                  htmlFor="share-national-id"
                  className="text-sm font-medium text-slate-700"
                >
                  {t('sharedPage.nationalIdLabel')}
                </label>
                <input
                  id="share-national-id"
                  type="text"
                  required
                  value={nationalId}
                  onChange={e => setNationalId(e.target.value)}
                  // No autocapitalize fight and no spellcheck squiggle on an ID
                  // number. The server folds case and punctuation anyway.
                  autoCapitalize="characters"
                  autoCorrect="off"
                  spellCheck={false}
                  autoComplete="off"
                  aria-invalid={attemptFailed}
                  aria-describedby="share-national-id-hint"
                  className={fieldClass}
                />
                <span
                  id="share-national-id-hint"
                  className="text-xs text-slate-500"
                >
                  {t('sharedPage.nationalIdHint')}
                </span>
              </div>
            )}

            {attemptFailed && (
              <p
                id="share-unlock-error"
                role="alert"
                className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700"
              >
                {needsNationalId
                  ? t('sharedPage.unlockFailedWithId')
                  : t('sharedPage.unlockFailed')}
              </p>
            )}

            <button
              type="submit"
              disabled={
                !dob || submitting || (needsNationalId && !nationalId.trim())
              }
              className="w-full rounded-lg bg-blue-600 px-4 py-2.5 text-base font-semibold text-white transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-300 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting
                ? t('sharedPage.opening')
                : t('sharedPage.openReport')}
            </button>

            {/* Said before she opens it, not after. Once she has, the link is
                spent and telling her then is too late to be useful. */}
            <p className="border-t border-slate-100 pt-4 text-xs leading-relaxed text-slate-500">
              {t('sharedPage.singleUseNotice')}
            </p>
          </form>
        </div>
      </StatusShell>
    );
  }

  if (phase === 'loading') {
    return <PendingScreen label={t('sharedPage.loadingReport')} />;
  }

  if (phase === 'failed') {
    return (
      <StatusScreen
        tone="problem"
        icon={ICON_CLOUD_OFF}
        title={t('sharedPage.loadFailedTitle')}
        body={t('sharedPage.loadFailedBody')}
        action={
          <button
            type="button"
            onClick={handleRetry}
            className="w-full rounded-lg bg-blue-600 px-4 py-2.5 text-base font-semibold text-white transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-300 focus:ring-offset-2"
          >
            {t('sharedPage.tryAgain')}
          </button>
        }
      />
    );
  }

  /* ---------- ready: images-only ---------- */
  if (scope === 'images_only') {
    return (
      <Shell>
        <h1 className="text-xl font-semibold text-slate-900">
          {t('sharedPage.reportTitle')}
        </h1>
        <Card>
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
              {t('sharedPage.images', { count: previews.length })}
            </h2>
            {previews.length > 1 && (
              <button
                type="button"
                onClick={() => void downloadAllImages()}
                disabled={downloading}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-300 disabled:opacity-60"
              >
                {downloading
                  ? t('sharedPage.preparingImages')
                  : t('sharedPage.saveAllImages')}
              </button>
            )}
          </div>

          {downloadFailed && (
            <p role="alert" className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
              {t('sharedPage.imageDownloadFailed')}
            </p>
          )}

          {previews.length === 0 ? (
            <p className="text-sm text-slate-500">{t('sharedPage.noImages')}</p>
          ) : (
            // object-contain, not cover: cropping an ultrasound to fit a tile
            // can cut out the very thing the image was kept for.
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {previews.map((p, i) => (
                <figure key={i} className="flex flex-col gap-2">
                  <img
                    src={p.url}
                    alt={t('sharedPage.imageAlt', { index: i + 1 })}
                    loading="lazy"
                    className="w-full rounded-lg bg-slate-900 object-contain"
                  />
                  <button
                    type="button"
                    onClick={() => void downloadImage(p.url, i + 1)}
                    className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-300"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                      className="h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M12 3v12m0 0 4-4m-4 4-4-4M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
                    </svg>
                    {t('sharedPage.saveImage')}
                  </button>
                </figure>
              ))}
            </div>
          )}
        </Card>
        <div className="flex flex-col gap-3 px-1 pb-4">
          <p className="text-xs leading-relaxed text-slate-500">
            {t('sharedPage.footerNote')}
          </p>
          <button
            type="button"
            onClick={handleCloseSession}
            className="self-start rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-white focus:outline-none focus:ring-2 focus:ring-blue-300"
          >
            {t('sharedPage.closeSession')}
          </button>
        </div>
      </Shell>
    );
  }

  /* ---------- ready: the report itself ---------- */
  return (
    <div className="min-h-screen w-full bg-slate-100">
      <header className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-slate-200 bg-white px-4 py-3">
        <h1 className="truncate text-base font-semibold text-slate-900">
          {t('sharedPage.reportTitle')}
        </h1>
        <button
          type="button"
          onClick={() => void handleDownload()}
          disabled={downloading}
          className="shrink-0 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-300 disabled:opacity-60"
        >
          {downloading ? t('sharedPage.preparingPdf') : t('sharedPage.downloadPdf')}
        </button>
      </header>

      {downloadFailed && (
        <p role="alert" className="bg-red-50 px-4 py-2 text-sm text-red-700">
          {t('sharedPage.downloadFailed')}
        </p>
      )}

      {/* srcdoc rather than innerHTML: the report carries its own stylesheet,
          which would otherwise restyle this page. `sandbox` without
          allow-scripts blocks execution, and keeping allow-same-origin is what
          lets fitFrame measure the content. */}
      <iframe
        ref={frameRef}
        srcDoc={html ?? ''}
        onLoad={fitFrame}
        sandbox="allow-same-origin"
        title={t('sharedPage.reportTitle')}
        className="block w-full border-0 bg-white"
      />

      <footer className="flex flex-col gap-3 border-t border-slate-200 bg-white px-4 py-4">
        <p className="text-xs leading-relaxed text-slate-500">
          {t('sharedPage.footerNote')}
        </p>
        <button
          type="button"
          onClick={handleCloseSession}
          className="self-start rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-300"
        >
          {t('sharedPage.closeSession')}
        </button>
      </footer>
    </div>
  );
}

export default SharedReportPage;
